
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Users, User, Brain, CheckCircle } from 'lucide-react';
import { ComparisonData, getTraitDescription } from '@/utils/comparisonAnalysis';

interface ComparisonDashboardProps {
  data: ComparisonData;
  onBack: () => void;
}

interface EnhancedInsights {
  summary_points?: string[];
  detailed_analysis?: string[];
  strengths: string[];
  challenges: string[];
  recommendations: string[];
}

const ComparisonDashboard: React.FC<ComparisonDashboardProps> = ({ data, onBack }) => {
  const [isRegeneratingInsights, setIsRegeneratingInsights] = useState(false);
  const [currentInsights, setCurrentInsights] = useState<EnhancedInsights>(data.insights);

  const getCompatibilityColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-950 dark:border-green-800';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-950 dark:border-yellow-800';
    return 'text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-950 dark:border-red-800';
  };

  const getCompatibilityLabel = (score: number) => {
    if (score >= 80) return 'High Compatibility';
    if (score >= 60) return 'Good Compatibility';
    return 'Needs Attention';
  };

  const getTraitLevel = (score: number) => {
    // This function now receives percentage scores (0-100) from data.person.scores
    if (score > 70) return 'High';
    if (score < 30) return 'Low';
    return 'Moderate';
  };

  // Determine if we have the new format or legacy format
  const hasNewFormat = currentInsights.summary_points && currentInsights.detailed_analysis;
  
  // For legacy format, combine all insights into a single narrative
  const combinedInsights = hasNewFormat 
    ? [...(currentInsights.summary_points || []), ...(currentInsights.detailed_analysis || [])]
    : [
        ...currentInsights.strengths,
        ...currentInsights.challenges,
        ...currentInsights.recommendations
      ];

  const regenerateInsights = async () => {
    setIsRegeneratingInsights(true);
    try {
      console.log('🔄 Regenerating AI insights...');
      const { generateInsights } = await import('@/utils/comparisonAnalysis');
      const newInsights = await generateInsights(
        { full_name: data.person1.name, scores: data.person1.scores },
        { full_name: data.person2.name, scores: data.person2.scores },
        data.compatibility
      );
      setCurrentInsights(newInsights);
      console.log('✅ Insights regenerated successfully');
    } catch (error) {
      console.error('❌ Failed to regenerate insights:', error);
    } finally {
      setIsRegeneratingInsights(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            onClick={onBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Selection
          </Button>
        </div>

        {/* Header */}
        <Card className="border border-border shadow-lg mb-6">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-3 text-2xl font-bold text-foreground">
              <Users className="h-6 w-6 text-primary" />
              {data.person1.name} vs {data.person2.name}
            </CardTitle>
            <div className="flex items-center justify-center gap-4 mt-4">
              <Badge 
                variant="secondary" 
                className={`px-4 py-2 text-lg font-semibold ${getCompatibilityColor(data.compatibility.overall)}`}
              >
                {Math.round(data.compatibility.overall)}% Overall Compatibility
              </Badge>
              <span className="text-sm text-muted-foreground">
                {getCompatibilityLabel(data.compatibility.overall)}
              </span>
            </div>
          </CardHeader>
        </Card>

        {/* Inline Profile Summaries */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Person 1 Profile */}
          <Card className="border border-border shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary"></div>
                <User className="h-5 w-5 text-primary" />
                {data.person1.name}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{data.person1.email}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Drive (D)</p>
                  <p className="font-semibold text-primary">{getTraitLevel(data.person1.scores.dominance)}</p>
                  <p className="text-xs text-muted-foreground">({data.person1.scores.dominance}%)</p>
                </div>
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Connection (C)</p>
                  <p className="font-semibold text-primary">{getTraitLevel(data.person1.scores.extraversion)}</p>
                  <p className="text-xs text-muted-foreground">({data.person1.scores.extraversion}%)</p>
                </div>
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Task (T)</p>
                  <p className="font-semibold text-primary">{getTraitLevel(data.person1.scores.patience)}</p>
                  <p className="text-xs text-muted-foreground">({data.person1.scores.patience}%)</p>
                </div>
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Order (O)</p>
                  <p className="font-semibold text-primary">{getTraitLevel(data.person1.scores.formality)}</p>
                  <p className="text-xs text-muted-foreground">({data.person1.scores.formality}%)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Person 2 Profile */}
          <Card className="border border-border shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-secondary"></div>
                <User className="h-5 w-5 text-secondary-foreground" />
                {data.person2.name}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{data.person2.email}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Drive (D)</p>
                  <p className="font-semibold text-secondary-foreground">{getTraitLevel(data.person2.scores.dominance)}</p>
                  <p className="text-xs text-muted-foreground">({data.person2.scores.dominance}%)</p>
                </div>
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Connection (C)</p>
                  <p className="font-semibold text-secondary-foreground">{getTraitLevel(data.person2.scores.extraversion)}</p>
                  <p className="text-xs text-muted-foreground">({data.person2.scores.extraversion}%)</p>
                </div>
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Task (T)</p>
                  <p className="font-semibold text-secondary-foreground">{getTraitLevel(data.person2.scores.patience)}</p>
                  <p className="text-xs text-muted-foreground">({data.person2.scores.patience}%)</p>
                </div>
                <div className="text-center p-2 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Order (O)</p>
                  <p className="font-semibold text-secondary-foreground">{getTraitLevel(data.person2.scores.formality)}</p>
                  <p className="text-xs text-muted-foreground">({data.person2.scores.formality}%)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Commentary Box */}
        <Card className="border border-border shadow-lg bg-muted/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-xl font-bold text-foreground">
              <Brain className="h-6 w-6 text-primary" />
              AI Analysis & Recommendations
              <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground ml-auto">
                AI Generated
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                onClick={regenerateInsights}
                disabled={isRegeneratingInsights}
                variant="outline"
                size="sm"
                className="text-xs"
              >
                {isRegeneratingInsights ? (
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin"></div>
                    Regenerating...
                  </div>
                ) : (
                  'Regenerate Analysis'
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasNewFormat && currentInsights.summary_points ? (
              <>
                {/* Key Insights Section */}
                <div className="bg-card p-4 rounded-lg border-l-4 border-green-500">
                  <h4 className="flex items-center gap-2 text-md font-semibold text-foreground mb-3">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Key Insights
                  </h4>
                  <div className="space-y-2">
                    {currentInsights.summary_points.map((point, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0"></div>
                        <p className="text-foreground leading-relaxed text-sm">{point}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Detailed Analysis Section */}
                <div className="bg-card p-4 rounded-lg border-l-4 border-primary">
                  <h4 className="flex items-center gap-2 text-md font-semibold text-foreground mb-3">
                    <Brain className="h-4 w-4 text-primary" />
                    Detailed Analysis
                  </h4>
                  <div className="space-y-3">
                    {currentInsights.detailed_analysis?.map((insight, index) => (
                      <div key={index} className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-foreground leading-relaxed">{insight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              // Legacy format display
              combinedInsights.length > 0 ? (
                combinedInsights.map((insight, index) => (
                  <div key={index} className="bg-card p-4 rounded-lg border-l-4 border-primary shadow-sm">
                    <p className="text-foreground leading-relaxed">{insight}</p>
                  </div>
                ))
              ) : (
                <div className="bg-card p-4 rounded-lg border-l-4 border-muted shadow-sm">
                  <p className="text-muted-foreground italic">Generating personalized insights...</p>
                </div>
              )
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ComparisonDashboard;
